import { useParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { PAGES, IMAGES } from "@/data/pages";
import { SITE } from "@/config/site";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import Reveal from "@/components/Reveal";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import FaqAccordion from "@/components/FaqAccordion";
import NotFoundPage from "@/pages/NotFoundPage";

const breadcrumbLd = (page) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE.url}/` },
    { "@type": "ListItem", position: 2, name: page.h1, item: `${SITE.url}${page.path}` },
  ],
});

const faqLd = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
});

const ContentPage = ({ slug: slugProp }) => {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = PAGES[slug];
  if (!page) return <NotFoundPage />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [breadcrumbLd(page), ...(page.faqs ? [faqLd(page.faqs)] : [])].map(({ "@context": _c, ...rest }) => rest),
  };

  return (
    <main data-testid={`page-${slug}`}>
      <Seo title={page.seoTitle} description={page.seoDescription} path={page.path} jsonLd={jsonLd} />

      <section className="relative overflow-hidden bg-brand-teal text-white grain">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-brand-gold/20 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <Breadcrumbs items={[{ label: page.h1 }]} />
          <div className="mt-8 grid items-center gap-10 lg:grid-cols-2">
            <Reveal>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-gold">{page.eyebrow}</p>
              <h1 className="mt-3 font-display text-4xl sm:text-5xl font-bold tracking-tight leading-tight">{page.h1}</h1>
              {page.intro.map((p) => (
                <p key={p.slice(0, 24)} className="mt-4 text-base sm:text-lg text-white/80 leading-relaxed">{p}</p>
              ))}
              <WhatsAppCTA testid={`cta-${slug}-hero`} className="mt-7" variant="gold" />
            </Reveal>
            <Reveal delay={0.15} className="hidden lg:block">
              <div className="relative">
                <div className="absolute -inset-3 rounded-[2rem] border-2 border-brand-gold/40" aria-hidden="true" />
                <img
                  src={IMAGES[page.image]}
                  alt={page.imageAlt}
                  width="800"
                  height="560"
                  loading="eager"
                  className="relative rounded-[2rem] object-cover w-full h-[380px] shadow-2xl"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 space-y-12">
        {page.sections.map((section, i) => (
          <Reveal key={section.heading} delay={0.05}>
            <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-brand-charcoal">
              <span className="mr-3 inline-block h-8 w-1.5 rounded-full bg-brand-gold align-middle" aria-hidden="true" />
              {section.heading}
            </h2>
            {section.paragraphs?.map((p) => (
              <p key={p.slice(0, 24)} className="mt-4 text-base text-slate-600 leading-relaxed">{p}</p>
            ))}
            {section.list && (
              <ul className="mt-4 space-y-2.5">
                {section.list.map((item) => (
                  <li key={item} className="flex gap-2.5 text-base text-slate-600 leading-relaxed">
                    <CheckCircle2 className="mt-1 h-5 w-5 shrink-0 text-brand-sage" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            )}
          </Reveal>
        ))}
      </section>

      {page.faqs && (
        <section className="bg-white py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal>
              <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-brand-charcoal text-center">
                Frequently Asked Questions
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-8">
              <FaqAccordion faqs={page.faqs} testid={`faq-${slug}`} />
            </Reveal>
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24">
        <Reveal>
          <div className="rounded-3xl bg-brand-teal grain relative overflow-hidden px-6 sm:px-12 py-12 text-center text-white">
            <div className="absolute -bottom-24 -left-24 h-72 w-72 rounded-full bg-brand-coral/20 blur-3xl" aria-hidden="true" />
            <h2 className="relative font-display text-2xl sm:text-3xl font-semibold">Need Help Finding a Nursing Home in Penang?</h2>
            <p className="relative mx-auto mt-3 max-w-2xl text-white/80 leading-relaxed">
              Tell us your preferred location, care requirements and budget. We will help you explore suitable options
              and coordinate the next steps — free for families.
            </p>
            <WhatsAppCTA testid={`cta-${slug}-bottom`} variant="gold" className="relative mt-7" />
            <div className="relative mt-8 flex flex-wrap justify-center gap-4 text-sm">
              {page.related.map((r) => (
                <a key={r.href + r.label} href={r.href} className="rounded-full border border-white/25 px-4 py-2 text-white/80 hover:border-brand-gold hover:text-brand-gold transition-colors">
                  {r.label}
                </a>
              ))}
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
};

export default ContentPage;
